import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { endpoints } from "../../endpoints";
import { useLazyPaginatedChoices } from "../../util/attributeChoices";
import { formatNumericFilterCriterion } from "../Filters/filterutils";
import type {
  AdvancedFilterCard,
  AdvancedFilterCollectionInstance,
  AdvancedFilterQuantitativeCriterion,
  NumericFilterCriterion,
  WithPagination,
} from "../../types/types";

/**
 * The reserved key under which the whole
 * set of criterion cards is stored inside the existing flat `Filters` map
 * (`{[key]: string[]}`, see `filterStateManager.ts`) — one more entry in
 * that map, not a parallel state channel, so `loadFilters`/`saveFilters`/the
 * URL-vs-sessionStorage size threshold all keep working unmodified. Kept
 * separate from ordinary filter-type keys (which are attribute/facet
 * slugs) so it can never collide with one.
 *
 */
export const ADVANCED_FILTERS_KEY = "__advanced_filters";

const OTHER_FILTER_CHOICES_PAGE_SIZE = 20;

/**
 * FE-internal only — encodes the whole set of criterion cards as a single
 * JSON string, stored as the sole element of `ADVANCED_FILTERS_KEY`'s array.
 * Unlike `encodeNumericFilterCriterion` (a single operator+value string that
 * must coexist with chip arrays under attribute-keyed filter types and is
 * therefore detected by attempting to decode it), this lives under its own
 * reserved key, so there's no ambiguity to resolve on decode.
 */
export function encodeAdvancedFilterCards(cards: AdvancedFilterCard[]): string {
  return JSON.stringify(cards);
}

/** Inverse of `encodeAdvancedFilterCards`. Returns null for missing/malformed input rather than throwing. */
export function decodeAdvancedFilterCards(
  raw: string | undefined
): AdvancedFilterCard[] | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AdvancedFilterCard[]) : null;
  } catch {
    return null;
  }
}

/** A card is uniquely identified by (collection_instance_id, key_attribute_value) — see AdvancedFiltersModal.tsx. */
export function advancedFilterCardKey(
  collectionInstanceId: string,
  keyAttributeValue: string
): string {
  return `${collectionInstanceId}::${keyAttributeValue}`;
}

export function findAdvancedFilterCardIndex(
  cards: AdvancedFilterCard[],
  collectionInstanceId: string,
  keyAttributeValue: string
): number {
  return cards.findIndex(
    (card) =>
      card.collection_instance_id === collectionInstanceId &&
      card.key_attribute_value === keyAttributeValue
  );
}

/**
 * Fetches every `is_valid` collection instance for the storefront — the
 * modal's left-pane groups and, at the page level, the second half of the
 * "Advanced Filters" button's gate (§7.2 of the FE plan: flag on AND ≥1
 * valid instance configured). Deliberately the same SWR key shape
 * (`valid_only: true`) everywhere it's called so SWR dedupes the request
 * instead of every caller re-fetching.
 */
export function useAdvancedFilterCollectionInstances(
  storefrontId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  // The whole `{ params }` wrapper — not just `params` — must be memoized:
  // SWR (0.3.11) hashes object key segments by reference identity (see
  // node_modules/swr/esm/libs/hash.js), so a fresh `{ params }` literal on
  // every render would hash to a new cache key every time and refetch on
  // every render regardless of `params` itself being stable.
  const requestConfig = useMemo(
    () => ({ params: { limit: 100, valid_only: true } }),
    []
  );
  const { data, error } = useSWR<
    WithPagination<{ data: AdvancedFilterCollectionInstance[] }>
  >(
    enabled && storefrontId
      ? [
          endpoints.v2_storefronts_id_advancedFilters_collectionInstances(
            storefrontId
          ),
          requestConfig,
        ]
      : null
  );

  return {
    instances: data?.data ?? [],
    isLoading: enabled && !!storefrontId && !data && !error,
    error,
  };
}

/**
 * `NumericFilterInput` (reused unmodified from Part 1) speaks
 * `NumericFilterCriterion` (numbers, `operator`); the wire shape for a
 * quantitative card entry is `AdvancedFilterQuantitativeCriterion` (strings,
 * `op` — precision-safe over JSON floats, same rule as Part 1's `applied`).
 * These two converters are the only place that translation happens.
 */
export function quantitativeCriterionToNumericFilterCriterion(
  criterion: AdvancedFilterQuantitativeCriterion | undefined
): NumericFilterCriterion | null {
  if (!criterion) return null;
  // A `switch` on the discriminant, not `if`/`else` — each variant's `op` is
  // itself a multi-literal union, and (per `NumericFilterCriterion`'s own
  // doc comment on the same gotcha) TypeScript doesn't reliably narrow the
  // negated branch of an `||` equality chain against a multi-literal
  // discriminant. Per-case `switch` narrowing doesn't have this problem.
  switch (criterion.op) {
    case "is":
    case "is_exactly":
      return { operator: criterion.op, value: Number(criterion.value) };
    case "between":
    case "overlaps":
      return {
        operator: criterion.op,
        min: criterion.min !== undefined ? Number(criterion.min) : undefined,
        max: criterion.max !== undefined ? Number(criterion.max) : undefined,
      };
  }
}

export function numericFilterCriterionToQuantitativeCriterion(
  quantitativeFilterId: string,
  criterion: NumericFilterCriterion
): AdvancedFilterQuantitativeCriterion {
  if (criterion.operator === "is" || criterion.operator === "is_exactly") {
    return {
      quantitative_filter_id: quantitativeFilterId,
      op: criterion.operator,
      value: String(criterion.value),
    };
  }
  return {
    quantitative_filter_id: quantitativeFilterId,
    op: criterion.operator,
    min: criterion.min !== undefined ? String(criterion.min) : undefined,
    max: criterion.max !== undefined ? String(criterion.max) : undefined,
  };
}

/**
 * Per the BE plan's §2.2/§8 "one-sided `between` doesn't fall back to the
 * stored range's other bound" gap (see the FE plan's §9g): a one-sided
 * range (only Min or only Max filled) almost always means an open-ended
 * "at least X"/"at most X" query, which is exactly what `overlaps` resolves
 * correctly and `between` under-matches. A closed range (both bounds
 * filled) is unaffected — `between` is exact there.
 *
 * Must only run on the outgoing request body, never on the stored cards:
 * the stored cards seed `NumericFilterInput`'s operator dropdown, so
 * rewriting them would flip it from "Between" to "Overlaps" the moment the
 * user fills the first bound, before they've had a chance to fill the second.
 */
export function compileAdvancedFilterCardsForRequest(
  cards: AdvancedFilterCard[]
): AdvancedFilterCard[] {
  return cards.map((card) => ({
    ...card,
    quantitative: card.quantitative.map(
      (criterion): AdvancedFilterQuantitativeCriterion =>
        criterion.op === "between" &&
        (criterion.min === undefined) !== (criterion.max === undefined)
          ? { ...criterion, op: "overlaps" }
          : criterion
    ),
  }));
}

/**
 * The selected-filters chip label for one card:
 * `<Key Attribute>: <value>; <Filter>: <criterion>; ...`, in the instance's
 * own mapping order (the same order the modal's card renders its rows in).
 * Criteria whose mapping is no longer on the instance are omitted.
 */
export function formatAdvancedFilterCardLabel(
  card: AdvancedFilterCard,
  instance: AdvancedFilterCollectionInstance,
  t: TFunction
): string {
  const segments = [
    `${instance.key_attribute_display_name}: ${card.key_attribute_value}`,
  ];

  instance.quantitative_filters.forEach((filter) => {
    const criterion = quantitativeCriterionToNumericFilterCriterion(
      card.quantitative.find((c) => c.quantitative_filter_id === filter.id)
    );
    if (!criterion) return;
    if (
      (criterion.operator === "between" || criterion.operator === "overlaps") &&
      criterion.min === undefined &&
      criterion.max === undefined
    ) {
      return;
    }
    segments.push(
      `${filter.display_name}: ${formatNumericFilterCriterion(criterion, t)}`
    );
  });

  instance.other_filters.forEach((filter) => {
    const values = card.other.find(
      (c) => c.other_filter_id === filter.id
    )?.values;
    if (!values?.length) return;
    segments.push(`${filter.attribute_display_name}: ${values.join(", ")}`);
  });

  return segments.join("; ");
}

/**
 * The value options for one Other Filter row of one criterion card.
 *
 * Deliberately not the attribute's own list choices
 * (`useLazyListBackedChoices` over `/pim/lists/{list_id}/choices`, which was
 * what Part 2 read): a list holds every item ever defined on it, so that
 * dropdown offered values no product in the configured collection records —
 * picking one returned nothing. This endpoint returns only the items products
 * actually carry in that collection, narrowed to the card's own Key Attribute
 * value and row-scoped exactly as the submitted card will be, so every option
 * offered matches at least one product.
 */
export function useAdvancedFilterOtherFilterChoices({
  storefrontId,
  instanceId,
  otherFilterId,
  keyAttributeValue,
}: {
  storefrontId: string | undefined;
  instanceId: string;
  otherFilterId: string;
  keyAttributeValue: string;
}) {
  const { t } = useTranslation();

  const url = storefrontId
    ? endpoints.v2_storefronts_id_advancedFilters_collectionInstances_id_otherFilters_id_choices(
        storefrontId,
        instanceId,
        otherFilterId
      )
    : null;

  const existingParams = new URLSearchParams({
    order_by: "asc",
    key_attribute_value: keyAttributeValue,
  });

  const { loadedChoices, ...rest } = useLazyPaginatedChoices({
    url,
    cacheScope: `${instanceId}:${otherFilterId}`,
    existingParams,
    errorMessage: t("There was an error loading filter values"),
    pageSize: OTHER_FILTER_CHOICES_PAGE_SIZE,
  });

  return { choices: loadedChoices, ...rest };
}
